/**
 * CSV export for the CRM list views.
 *
 * Two things here are not incidental formatting — they are the difference
 * between an export that reconciles and one that quietly lies.
 *
 * 1. IDENTIFIERS WITH LEADING ZEROS. MID numbers look like "019100114637".
 *    Excel reads an unadorned digit string as a number and drops the leading
 *    zero, so the exported MID no longer matches the processor's records — and
 *    nothing about the file looks wrong. Columns that carry an identifier
 *    declare `exportAs: "text"` and are emitted as ="019100114637", which Excel
 *    and Sheets both read back as the literal string.
 *
 * 2. FORMULA INJECTION. DBA and merchant names are merchant-supplied and land
 *    in a file an internal user opens in Excel. A value starting with = + - or @
 *    is executed as a formula there. Those are prefixed with a single quote so
 *    they render as text. This costs nothing on the ~0 rows that begin with an
 *    operator and removes the whole class.
 */

const FORMULA_TRIGGERS = ["=", "+", "-", "@", "\t", "\r"];

/**
 * One field, escaped and quoted for CSV.
 * @param {*} value
 * @param {"text"|undefined} exportAs  "text" forces Excel to preserve the value
 *   verbatim — use for identifiers whose leading zeros or length would otherwise
 *   be reinterpreted as a number.
 */
export function escapeCsvValue(value, exportAs) {
  if (value === null || value === undefined) return "";

  let str = String(value);

  if (exportAs === "text") {
    // ="..." survives Excel's numeric coercion. Inner quotes are doubled twice
    // over: once for the formula string, once for the CSV field.
    return `"=""${str.replace(/"/g, '""""')}"""`;
  }

  if (str.length && FORMULA_TRIGGERS.includes(str[0])) {
    str = `'${str}`;
  }

  // Quote whenever the field could otherwise break the row apart.
  if (/[",\n\r]/.test(str)) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

/**
 * Build a CSV document from rows and a column model.
 *
 * Columns are the SAME objects the table renders from, so an export inherits the
 * table's audience gating for free — a column hidden from processor users
 * (SOUND-570) is absent from the model and therefore absent from the file. That
 * property is the reason this takes columns rather than a field list.
 *
 * A column contributes a value from `exportValue(row)`, falling back to
 * `row[col.key]`. It must NOT fall back to `cell(row)`, which returns React
 * elements for status pills and badges.
 */
export function toCsv(rows = [], columns = []) {
  const cols = columns.filter((c) => c && c.key && c.exportable !== false);
  const header = cols.map((c) => escapeCsvValue(c.exportHeader || c.header || c.key));

  const lines = [header.join(",")];
  for (const row of rows) {
    lines.push(
      cols
        .map((c) => {
          const value = c.exportValue ? c.exportValue(row) : row[c.key];
          return escapeCsvValue(value, c.exportAs);
        })
        .join(",")
    );
  }
  // CRLF is what RFC 4180 specifies and what Excel is happiest with.
  return lines.join("\r\n");
}

/** `mids-2026-08-15.csv` */
export function csvFilename(base, date = new Date()) {
  const stamp = date.toISOString().slice(0, 10);
  return `${base}-${stamp}.csv`;
}

/**
 * Hand the file to the browser. Separate from toCsv so the document builder
 * stays pure and testable — this half touches the DOM and cannot be.
 */
export function downloadCsv(filename, text) {
  // The BOM is what makes Excel open UTF-8 correctly; without it, merchant names
  // with accented characters arrive mojibaked.
  const blob = new Blob(["﻿", text], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
